import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { GlassCard, GradientBackground } from '@/components/shared/redesign';
import { Button } from '@/components/common/admin/mobile/Button';
import { TransactionTable } from './TransactionTable';
import { IAdminTransaction } from '@/types/admin/transactions';

interface TransactionHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  transactions: IAdminTransaction[];
  residentName?: string;
}

export const TransactionHistoryModal: React.FC<TransactionHistoryModalProps> = ({
  visible,
  onClose,
  transactions,
  residentName,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const styles = useStyles();

  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
    }
  }, [visible, transactions.length]);

  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <GlassCard style={styles.modalContainer} padding={0}>
          <GradientBackground>
            {/* Header */}
            <View style={styles.header}>
              <View>
                <Text style={styles.title}>Transaction History</Text>
                {residentName && (
                  <Text style={styles.subtitle}>{residentName}</Text>
                )}
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                  <Text style={styles.closeButtonText}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Transaction Table */}
            <View style={styles.tableContainer}>
              <TransactionTable
                transactions={paginatedTransactions}
                maxRows={itemsPerPage}
                showPager={false}
              />
            </View>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <View style={styles.paginationContainer}>
                <Text style={styles.paginationInfo}>
                  Showing {startIndex + 1} - {Math.min(endIndex, transactions.length)} of {transactions.length} transactions
                </Text>
                <View style={styles.paginationButtons}>
                  <Button
                    title="Previous"
                    onPress={handlePreviousPage}
                    disabled={currentPage === 1}
                    variant={currentPage === 1 ? 'secondary' : 'primary'}
                  />
                  <Text style={styles.pageIndicator}>
                    Page {currentPage} of {totalPages}
                  </Text>
                  <Button
                    title="Next"
                    onPress={handleNextPage}
                    disabled={currentPage === totalPages}
                    variant={currentPage === totalPages ? 'secondary' : 'primary'}
                  />
                </View>
              </View>
            )}
          </GradientBackground>
        </GlassCard>
      </View>
    </Modal>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
      },
      modalContainer: {
        width: '95%',
        maxWidth: 1200,
        height: '90%',
        backgroundColor: colors.bg.modal,
      },
      header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 24,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      title: {
        fontSize: 24,
        fontWeight: '700',
        color: colors.text.primary,
        marginBottom: 4,
      },
      subtitle: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      headerActions: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
      },
      closeButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
      },
      closeButtonText: {
        fontSize: 20,
        color: colors.text.primary,
        fontWeight: '600',
      },
      tableContainer: {
        flex: 1,
        padding: 16,
      },
      paginationContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
        backgroundColor: 'transparent',
      },
      paginationInfo: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      paginationButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
      },
      pageIndicator: {
        fontSize: 14,
        color: colors.text.primary,
        fontWeight: '500',
      },
    }),
  );
